import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { UserPlus, Trash2, KeyRound, Building2 } from 'lucide-react';

type HomeEntity = { id: string; name: string; province: string } | null;
type User = { id: string; username: string; displayName: string | null; role: string; createdAt: string; homeEntity: HomeEntity };
type CA = { id: string; name: string };

export default function UsersManager() {
  const [users, setUsers] = useState<User[]>([]);
  const [cas, setCAs] = useState<CA[]>([]);
  const [msg, setMsg] = useState('');

  // create form
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('ca_user');
  const [homeEntityId, setHomeEntityId] = useState('');

  const load = () => axios.get('/api/users').then(r => setUsers(r.data)).catch(e => setMsg(e.response?.data?.error || 'Failed to load users'));
  useEffect(() => {
    load();
    axios.get('/api/map/conservation-authorities').then(r => setCAs(r.data.map((c: any) => ({ id: c.id, name: c.name })).sort((a: CA, b: CA) => a.name.localeCompare(b.name)))).catch(() => {});
  }, []);

  const create = async () => {
    setMsg('');
    try {
      await axios.post('/api/users', {
        username: username.trim(), password, role,
        displayName: displayName.trim() || undefined,
        homeEntityId: role === 'ca_user' ? (homeEntityId || undefined) : undefined,
      });
      setUsername(''); setDisplayName(''); setPassword(''); setRole('ca_user'); setHomeEntityId('');
      setMsg('User created.');
      load();
    } catch (e: any) { setMsg(e.response?.data?.error || 'Failed to create user'); }
  };

  const setUserRole = (u: User, r: string) => axios.patch(`/api/users/${u.id}`, { role: r }).then(load).catch(() => {});
  const setUserHome = (u: User, id: string) => axios.patch(`/api/users/${u.id}`, { homeEntityId: id || null }).then(load).catch(() => {});
  const resetPw = (u: User) => {
    const pw = prompt(`New password for ${u.username} (min 8 chars):`);
    if (!pw) return;
    axios.patch(`/api/users/${u.id}`, { password: pw }).then(() => setMsg(`Password reset for ${u.username}.`)).catch(e => setMsg(e.response?.data?.error || 'Failed'));
  };
  const del = (u: User) => { if (window.confirm(`Delete user ${u.username}?`)) axios.delete(`/api/users/${u.id}`).then(load).catch(e => setMsg(e.response?.data?.error || 'Failed')); };

  const inp = 'bg-[var(--surface)] border border-[var(--border)] rounded-[8px] px-2 py-[7px] text-[13px] text-[color:var(--text-primary)] placeholder:text-[color:var(--text-muted)]';

  return (
    <div>
      <p className="text-[12px] text-[color:var(--text-muted)] m-0 mb-4">Create platform users. A <strong>Conservation Authority</strong> user gets a tailored experience anchored to their home CA. A <strong>Member</strong> has the same access as an admin, without the Admin, PDF Upload and Reports areas.</p>

      {/* Create */}
      <div className="grid gap-[8px] mb-4 [grid-template-columns:repeat(auto-fit,minmax(150px,1fr))] items-end">
        <input className={inp} placeholder="username" value={username} onChange={e => setUsername(e.target.value)} />
        <input className={inp} placeholder="display name (optional)" value={displayName} onChange={e => setDisplayName(e.target.value)} />
        <input className={inp} type="password" placeholder="initial password" value={password} onChange={e => setPassword(e.target.value)} />
        <select className={inp} value={role} onChange={e => setRole(e.target.value)}>
          <option value="ca_user">Conservation Authority</option>
          <option value="member">Member</option>
          <option value="admin">Admin</option>
        </select>
        {role === 'ca_user' && (
          <select className={inp} value={homeEntityId} onChange={e => setHomeEntityId(e.target.value)}>
            <option value="">— home CA —</option>
            {cas.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}
        <button onClick={create} disabled={!username.trim() || password.length < 8} className="inline-flex items-center justify-center gap-2 text-[13px] px-3 py-2 rounded-[10px] bg-[var(--accent)] text-white hover:opacity-90 disabled:opacity-50">
          <UserPlus size={15} /> Create
        </button>
      </div>
      {msg && <div className="text-[12px] text-[color:var(--text-muted)] mb-3">{msg}</div>}

      {/* List */}
      <div className="flex flex-col gap-2">
        {users.map(u => (
          <div key={u.id} className="flex items-center gap-3 flex-wrap border-b border-[var(--border)] pb-2 last:border-0">
            <div className="min-w-0 flex-1">
              <div className="text-[13px] text-[color:var(--text-primary)]">{u.username}{u.displayName ? <span className="text-[color:var(--text-muted)]"> · {u.displayName}</span> : ''}</div>
              {u.role === 'ca_user' && (
                <div className="text-[11px] text-[color:var(--text-muted)] inline-flex items-center gap-1"><Building2 size={11} />{u.homeEntity ? u.homeEntity.name : 'no home CA set'}</div>
              )}
            </div>
            <select className={inp} value={u.role} onChange={e => setUserRole(u, e.target.value)}>
              <option value="ca_user">Conservation Authority</option>
              <option value="member">Member</option>
              <option value="admin">Admin</option>
            </select>
            {u.role === 'ca_user' && (
              <select className={inp} value={u.homeEntity?.id || ''} onChange={e => setUserHome(u, e.target.value)}>
                <option value="">— home CA —</option>
                {cas.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            )}
            <button onClick={() => resetPw(u)} title="Reset password" className="text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]"><KeyRound size={15} /></button>
            <button onClick={() => del(u)} title="Delete" className="text-[color:var(--text-muted)] hover:text-[#f472b6]"><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
